import gettingStartedApi from './api/gettingStartedApi';

/**
 * Service for managing Getting Started progress via REST API
 */
export class GettingStartedService {
  /**
   * Get user's getting started progress
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, data: Object|null, error: string|null}>} Progress data
   */
  static async getUserProgress(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      const { data, error } = await gettingStartedApi.getUserProgress(userId);
      
      if (error) {
        throw new Error(error);
      }
      
      return {
        success: true,
        data: data?.data || null,
        error: null
      };
    } catch (error) {
      console.error('Error fetching getting started progress:', error);
      return {
        success: false,
        data: null,
        error: error.message || 'Failed to fetch progress'
      };
    }
  }

  /**
   * Mark a step as complete
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to mark as complete
   * @returns {Promise<{success: boolean, data: Object|null, error: string|null}>} Operation result
   */
  static async markStepComplete(userId, stepId) {
    try {
      if (!userId || stepId === undefined || stepId === null) {
        throw new Error('User ID and Step ID are required');
      }
      
      const { data, error } = await gettingStartedApi.markStepComplete(userId, stepId);
      
      if (error) {
        throw new Error(error);
      }
      
      return {
        success: true,
        data: data?.data || null,
        error: null
      };
    } catch (error) {
      console.error('Error marking step as complete:', error);
      return {
        success: false,
        data: null,
        error: error.message || 'Failed to mark step as complete'
      };
    }
  }

  /**
   * Reset a step to incomplete
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to reset
   * @returns {Promise<Object>} Operation result
   */
  static async resetStep(userId, stepId) {
    try {
      if (!userId || !stepId) {
        throw new Error('User ID and Step ID are required');
      }
      return await gettingStartedApi.resetStep(userId, stepId);
    } catch (error) {
      console.error('Error resetting step:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Initialize getting started steps for a new user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  static async initializeUserSteps(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      return await gettingStartedApi.initializeUserSteps(userId);
    } catch (error) {
      console.error('Error initializing getting started steps:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get getting started progress summary
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Summary data
   */
  static async getProgressSummary(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      return await gettingStartedApi.getProgressSummary(userId);
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Reset all getting started progress (except account creation)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  static async resetAllProgress(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      return await gettingStartedApi.resetAllProgress(userId);
    } catch (error) {
      console.error('Error resetting getting started progress:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Auto-complete step 2 when user creates their first API key
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  static async completeApiKeyStep(userId) {
    return this.markStepComplete(userId, 2);
  }

  /**
   * Auto-complete step 3 when user makes their first API call
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  static async completeFirstApiCallStep(userId) {
    return this.markStepComplete(userId, 3);
  }

  /**
   * Auto-complete step 4 when user configures webhooks
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  static async completeWebhookStep(userId) {
    return this.markStepComplete(userId, 4);
  }

  /**
   * Check if a specific step is completed
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to check
   * @returns {Promise<Object>} Check result
   */
  static async isStepCompleted(userId, stepId) {
    try {
      if (!userId || !stepId) {
        throw new Error('User ID and Step ID are required');
      }
      return await gettingStartedApi.isStepCompleted(userId, stepId);
    } catch (error) {
      console.error('Error checking step completion:', error);
      return {
        success: false,
        error: error.message,
        completed: false
      };
    }
  }

  /**
   * Get the next incomplete step
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Next step data
   */
  static async getNextStep(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      return await gettingStartedApi.getNextStep(userId);
    } catch (error) {
      console.error('Error fetching next step:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default GettingStartedService;
